import WRAP from "WRAP-JS";
import { COMMUNICATE_ERROR_MESSAGE_LIDEN, COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER } from "../../../constants/ErrorMessage";
import { formatLonLatDM, formatTimeZoneDate, translation } from "../../../utils/FPEUtils";
import { configLidenDh, configLidenLayer } from "./config/ConfigLiden";

class Liden {

  static NAME = 'LIDEN';
  static THUNDER_NAME = `${Liden.NAME}_THUNDER`;
  static THUNDERBOLT_NAME = `${Liden.NAME}_THUNDERBOLT`;

  static THUNDER_TYPES = {
    [Liden.THUNDER_NAME]: [0, 1],
    [Liden.THUNDERBOLT_NAME]: [4]
  };

  contentsVisibleState = {
    [Liden.THUNDER_NAME]: true,
    [Liden.THUNDERBOLT_NAME]: true
  };

  dh;
  layer;

  ready = false;
  dhAutoUpdate = true;
  visible;
  dispatcher;
  issueTime = '---';
  updateEndTime;
  targetTypes = [].concat.apply([], Object.keys(Liden.THUNDER_TYPES).map(key=>Liden.THUNDER_TYPES[key]));

  //自動更新フラグ
  autoRefresh = true;
  errorMessageInfo;

  constructor(timeController) {
    this.timeController = timeController;
    this.layer = new WRAP.Geo.Layer(Liden.NAME);
    this.layer.setVisible(false);
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    this.ready = true;
  }

  initData() {
    this.dh = new WRAP.Data(configLidenDh);

    this.dh.validate((displayTime, data, content) => {
      return this._validateTime(data, displayTime, content);
    });

    this.dh.updated('query', (ref) => {
      if (this.errorMessageInfo) {
        this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo});
        this.errorMessageInfo = null;
      }
      this._selectBaseValidTime(ref);
    }, () => {
      this.errorMessageInfo = this.dispatcher({type: 'SHOW_ERROR', payload: {
        message: COMMUNICATE_ERROR_MESSAGE_LIDEN,
        additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
      }});
    });
  }

  initLayer() {
    this.layer.configure(this.dh, configLidenLayer);
    this.layer.setTooltip(this._tooltip);
    this.layer.setAttributes({
      filter: this._visibleFilter.bind(this)
    });
  }

  initTimeController() {
    this.timeController.addLayer(this.layer);
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (!this.ready) {
      await this.initialize();
    }
    this.setVisible(this.visible);
  }

  _visibleFilter(feature) {
    if (!feature) return;
    return this.targetTypes.includes(feature.properties.THNDR_TYPE);
  }

  getLayer() {
    return this.layer;
  }

  setVisible(visible) {
    this.visible = visible;
    if (!this.ready) return;

    if (visible) {
      this.layer.show();
    } else {
      this.layer.hide();
    }

    //EXPO_RandomPointExtにすることで自動更新が動かなくなる問題のhack
    if (visible && this.autoRefresh) {
      const content = this.layer.get();
      delete content.begin_time;
      delete content.end_time;
      delete content.custom_time;
    }

    if (this.dhAutoUpdate !== visible) {
      this.dh.setAutoUpdate('data', visible && this.autoRefresh);
      this.dhAutoUpdate = visible;
    }

    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});

  }

  setContentsVisible(name, visible) {
    this.contentsVisibleState[name] = visible;
    this._updateTargetTypes();
  }

  getContentsVisibleState() {
    return this.contentsVisibleState;
  }

  changeRoutePointEditing(routePointEditing) {
    this.layer.disableInteraction(routePointEditing);
  }

  getTimeInformation() {
    return {
      id: Liden.NAME,
      label: 'LIDEN',
      baseTime: this.issueTime
    }
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) {
      return;
    }

    this.autoRefresh = autoFlg;

    if (!this.ready) {
      return;
    }

    if (autoFlg && this.visible) {
      const content = this.layer.get();
      delete content.begin_time;
      delete content.end_time;
      delete content.custom_time;
    }

    //コンテンツの自動更新を切り替え
    this.dh.setAutoUpdate('data', this.autoRefresh);
  }

  // getLegend() {
  //   return {
  //     id: Liden.NAME,
  //     label: 'LIDEN',
  //     component: LegendLiden,
  //     contents: this
  //   };
  // }

  _selectBaseValidTime(ref) {
    const updateEndTime = ref.end_time;
    if (this.autoRefresh) {
      //自動更新時のupdate_end_timeを更新
      this.updateEndTime = updateEndTime;
    }
    if (this.issueTime !== updateEndTime) {
      this.issueTime = updateEndTime;
      this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
    }

  }

  _updateTargetTypes() {
    this.targetTypes = [];
    if (this.contentsVisibleState[Liden.THUNDER_NAME]) {
      this.targetTypes = this.targetTypes.concat(Liden.THUNDER_TYPES[Liden.THUNDER_NAME]);
    }
    if (this.contentsVisibleState[Liden.THUNDERBOLT_NAME]) {
      this.targetTypes = this.targetTypes.concat(Liden.THUNDER_TYPES[Liden.THUNDERBOLT_NAME]);
    }
    this.layer.invalidate();
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(data, displayTime, content) {
    const now = new WRAP.Core.DateTime(this.updateEndTime);
    let dt = new WRAP.Core.DateTime(displayTime);

    //自動更新モード、displayTimeが未来時制のとき
    if (this.autoRefresh || now.diff(dt) > 0) {
      dt = now;
    }

    this.issueTime = content.custom_time = content.end_time = dt.text('YYYY-MM-DDThh:mm:ssZ');
    dt.add(-3600);
    content.begin_time = dt.text('YYYY-MM-DDThh:mm:ssZ');

    this.dispatcher({type: 'UPDATE_VALID_TIME', payload: this.getTimeInformation()});

    //↓TimeControllerに対応させるためのhack
    content.validtime = true;

    return true;
  }

  _tooltip(geo) {
    if (!geo?.properties) return;

    const p = geo.properties;

    const time = `${translation('Time')} : ${formatTimeZoneDate(p.obs_time)}`

    const lonLat = `${translation('Coordinates')} : ${formatLonLatDM(p.LOND, p.LATD)}`;

    let type = '';
    switch (p.THNDR_TYPE) {
      case 0:
      case 1:
        type = translation('Cloud-to-cloud lightning');
        break;
      case 4:
        type = translation('Cloud-to-ground lightning');
        break;
      default:
    }
    const thunderType = `${translation('Type')} : ${type}`;

    const thunderMultiple = `${translation('Multiple strokes')} : ${p.THNDR_MULT}`;

    const thunderElectricCurrent = p.THNDR_ELCCRT !== -999999999 ? `${translation('Discharge current')} : ${p.THNDR_ELCCRT}` : ''

    return `<ul>
        <li>${time}</li>
        <li>${lonLat}</li>
        <li>${thunderType}</li>
        <li>${thunderMultiple}</li>
        <li>${thunderElectricCurrent}</li>
    </ul>`;
  }
}

export default Liden;